import {
  Component,
  ElementRef,
  Injector,
  Input,
  OnChanges,
  OnInit,
  QueryList,
  SimpleChanges,
  ViewChildren
} from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { combineLatest } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ApAlertService, ApModalService } from 'webcommon/core-ui';
import { FcItemModalContext, FcItemModalMode } from '../../../models';
import { FcItemListBaseComponent, FcListValidationService } from '../../generic';
import { FcCopyContactInfoListModalComponent } from '../../fc-item-modals/fc-copy-contact-info-list-modal/fc-copy-contact-info-list-modal.component';
import { FcPhoneTypeListItemDto } from 'webcommon/shared';

@Component({
  providers: [FcListValidationService, ApAlertService],
  selector: 'fc-person-list',
  templateUrl: './fc-person-list.component.html',
  styleUrls: ['./fc-person-list.component.less']
})
export class FcPersonListComponent extends FcItemListBaseComponent implements OnChanges, OnInit {
  @ViewChildren('phoneNumber') phoneNumberInputs: QueryList<ElementRef<HTMLInputElement>>;
  @Input() baseId: string;
  @Input() form: FormGroup;
  @Input() listItem: any;
  @Input() isSubmitted = false;
  @Input() itemContext: FcItemModalContext;

  public birthMaxDate?: Date;
  public deathMinDate?: Date;
  public deathMaxDate = new Date();
  public preferredContactMethodItems: string[] = [];

  public listItemDefaultValidation = {
    Prefix: {
      MaxLength: 25,
    },
    FirstName: {
      IsRequired: true,
      MaxLength: 50,
    },
    MiddleName: {
      MaxLength: 50,
    },
    LastName: {
      IsRequired: true,
      MaxLength: 50,
    },
    Suffix: {
      MaxLength: 50,
    },
    MaidenName: {
      MaxLength: 50,
    },
    AkaFirstName: {
      MaxLength: 50,
    },
    AkaLastName: {
      MaxLength: 50,
    },
    SocialSecurityNumber: {
      MaxLength: 11,
      RegExp: '([0-9]{3}-[0-9]{2}-[0-9]{4})',
    },
    DriverLicenseNumber: {
      MaxLength: 25,
    },
    DriverLicenseState: {
      MaxLength: 3,
    },
    PhoneNumber: {
      MaxLength: 22,
      RegExp: '^(([0-9]{3}-[0-9]{4})|((\\([0-9]{3}\\)[0-9]{3}-[0-9]{4})( x[0-9]{1,7})?))$',
    },
    PhoneType: {},
    Notes: {
      MaxLength: 65535,
    },
  };

  // Name Details
  public nameDetailsFormGroup: FormGroup = new FormGroup({});

  // Other information
  public otherInformationFormGroup: FormGroup = new FormGroup({});

  // Addresses
  public undeliverableToPrimaryAddressControl: FormControl = new FormControl<boolean>(false);
  public primaryAddressFormGroup = new FormGroup<any>({});
  public secondaryAddressFormGroup = new FormGroup<any>({});

  // Contact Information
  public phonesFormArray = new FormArray([
    new FormGroup({
      phoneNumber: new FormControl<string>(''),
      phoneType: new FormControl<string>(''),
      phoneTypeUid: new FormControl<string[]>([]),
      marketing: new FormControl<boolean>(false),
      isPhoneSmsEnabled: new FormControl<boolean>(false)
    }),
    new FormGroup({
      phoneNumber: new FormControl<string>(''),
      phoneType: new FormControl<string>(''),
      phoneTypeUid: new FormControl<string[]>([]),
      marketing: new FormControl<boolean>(false),
      isPhoneSmsEnabled: new FormControl<boolean>(false)
    }),
    new FormGroup({
      phoneNumber: new FormControl<string>(''),
      phoneType: new FormControl<string>(''),
      phoneTypeUid: new FormControl<string[]>([]),
      marketing: new FormControl<boolean>(false),
      isPhoneSmsEnabled: new FormControl<boolean>(false)
    }),
    new FormGroup({
      phoneNumber: new FormControl<string>(''),
      phoneType: new FormControl<string>(''),
      phoneTypeUid: new FormControl<string[]>([]),
      marketing: new FormControl<boolean>(false),
      isPhoneSmsEnabled: new FormControl<boolean>(false)
    }),
  ]);
  public preferredContactMethodControl: FormControl = new FormControl<string>('');

  public emailsFormArray = new FormArray([
    new FormGroup({
      email: new FormControl<string>('', [Validators.email]),
      marketing: new FormControl<boolean>(false),
    }),
    new FormGroup({
      email: new FormControl<string>('', [Validators.email]),
      marketing: new FormControl<boolean>(false),
    }),
  ]);
  public declinedOrHasNoEmailAddressControl: FormControl = new FormControl<boolean>(false);

  public notesControl: FormControl = new FormControl<string>('');

  constructor(injector: Injector,
              private apModalService: ApModalService
  ) {
    super(injector);

    for (let i = 0; i < this.phonesFormArray.controls.length; i++) {
      const phoneNumberControl = this.phonesFormArray.controls[i].get('phoneNumber') as FormControl;
      const phoneTypeUidControl = this.phonesFormArray.controls[i].get('phoneTypeUid') as FormControl;
      const marketingControl = this.phonesFormArray.controls[i].get('marketing') as FormControl;
      phoneNumberControl?.valueChanges
        .pipe(
          distinctUntilChanged(),
          this.takeUntilDestroy(),
          debounceTime(10)
        ).subscribe((phoneNumber: string) => {
        // If Phone Number is not empty and valid add required validator to Phone Type
        if (typeof phoneNumber === 'string' && phoneNumber.length === 11) {
          this.phoneNumberInputs.get(i)?.nativeElement.setSelectionRange(phoneNumber.length + 3, phoneNumber.length + 3);
        }

        const validators = this.fcListValidation.getValidators(`PhoneType`);
        if (typeof phoneNumber === 'string' && phoneNumber.length > 0) {
          if (phoneNumberControl.valid) {
            validators.push(Validators.required);
          }
        } else {
          phoneTypeUidControl.setValue([]);
        }

        phoneTypeUidControl.setValidators(validators);
        phoneTypeUidControl.updateValueAndValidity();

        if (phoneNumber.length > 0 && marketingControl.disabled) {
          // Enable marketing when phone is not empty
          marketingControl.enable({emitEvent: false});
        } else if (phoneNumber.length === 0) {
          // Disable marketing and uncheck when phone is empty
          marketingControl.setValue(false, {emitEvent: false});
          marketingControl.disable({emitEvent: false});
        }
      });
    }

    combineLatest([
      this.primaryAddressFormGroup.valueChanges,
      this.secondaryAddressFormGroup.valueChanges,
      this.phonesFormArray.valueChanges,
      this.emailsFormArray.valueChanges,
    ]).pipe(
      distinctUntilChanged(),
      this.takeUntilDestroy(),
    ).subscribe(() => {
      const items = [];
      items.push('');

      if (this.isAddressPartiallyFilled(this.primaryAddressFormGroup)) {
        items.push('Primary Address');
      }
      if (this.isAddressPartiallyFilled(this.secondaryAddressFormGroup)) {
        items.push('Secondary Address');
      }

      for (let i = 0; i < this.phonesFormArray.controls.length; i++) {
        const phoneNumberControl = this.phonesFormArray.controls[i]?.get('phoneNumber') ?? null;
        if (phoneNumberControl == null || typeof phoneNumberControl.value !== 'string') return;
        if (phoneNumberControl.valid && phoneNumberControl.value.length > 0) {
          items.push(`Phone ${i + 1}`);
        }
      }
      for (let i = 0; i < this.emailsFormArray.controls.length; i++) {
        const emailControl = this.emailsFormArray.controls[i]?.get('email') ?? null;
        if (emailControl == null || typeof emailControl.value !== 'string') return;
        if (emailControl.valid && emailControl.value.length > 0) {
          items.push(`Email ${i + 1}`);
        }
      }
      items.push('Declined');
      this.preferredContactMethodItems = items;
    });

    this.subscribeEmails();
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.itemContext != null) {
      this.fcListValidation.initValidation(this.itemContext, this.listItemDefaultValidation);
    }
    if (changes.form != null) {
      this.addControlsToForm();
    }

    if (changes.itemContext != null || changes.form != null || changes.listItem != null) {
      this.setForm();
    }
  }

  public ngOnInit(): void {
    this.addUniqueIdToBaseId();
  }

  public setForm(): void {
    this.undeliverableToPrimaryAddressControl.setValue(this.listItem?.BadAddress1 ?? false);

    const phonesValue = [
      {
        phoneNumber: this.formatPhoneNumber(this.listItem?.Phone1?.PhoneNumber),
        phoneType: this.listItem?.Phone1?.PhoneType ?? '',
        phoneTypeUid: this.listItem?.Phone1?.PhoneTypeUid ? [this.listItem.Phone1.PhoneTypeUid] : [],
        marketing: this.listItem?.Phone1MessagingAllowed ?? false,
        isPhoneSmsEnabled: this.listItem?.Phone1SmsEnabledDefault ?? false
      },
      {
        phoneNumber: this.formatPhoneNumber(this.listItem?.Phone2?.PhoneNumber),
        phoneType: this.listItem?.Phone2?.PhoneType ?? '',
        phoneTypeUid: this.listItem?.Phone2?.PhoneTypeUid ? [this.listItem.Phone2.PhoneTypeUid] : [],
        marketing: this.listItem?.Phone2MessagingAllowed ?? false,
        isPhoneSmsEnabled: this.listItem?.Phone2SmsEnabledDefault ?? false
      },
      {
        phoneNumber: this.formatPhoneNumber(this.listItem?.Phone3?.PhoneNumber),
        phoneType: this.listItem?.Phone3?.PhoneType ?? '',
        phoneTypeUid: this.listItem?.Phone3?.PhoneTypeUid ? [this.listItem.Phone3.PhoneTypeUid] : [],
        marketing: this.listItem?.Phone3MessagingAllowed ?? false,
        isPhoneSmsEnabled: this.listItem?.Phone3SmsEnabledDefault ?? false
      },
      {
        phoneNumber: this.formatPhoneNumber(this.listItem?.Phone4?.PhoneNumber),
        phoneType: this.listItem?.Phone4?.PhoneType ?? '',
        phoneTypeUid: this.listItem?.Phone4?.PhoneTypeUid ? [this.listItem.Phone4.PhoneTypeUid] : [],
        marketing: this.listItem?.Phone4MessagingAllowed ?? false,
        isPhoneSmsEnabled: this.listItem?.Phone4SmsEnabledDefault ?? false
      },
    ];
    for (const phonesFormGroup of this.phonesFormArray.controls) {
      phonesFormGroup?.get('phoneNumber')?.setValidators(this.fcListValidation.getValidators('PhoneNumber'));
    }
    this.phonesFormArray.patchValue(phonesValue);

    const emailsValue = [
      {
        email: this.listItem?.Email1 ?? '',
        marketing: this.listItem?.Email1MessagingAllowed ?? false,
      },
      {
        email: this.listItem?.Email2 ?? '',
        marketing: this.listItem?.Email2MessagingAllowed ?? false,
      },
    ];
    this.emailsFormArray.patchValue(emailsValue);
    this.declinedOrHasNoEmailAddressControl.setValue(this.listItem?.NoEmailAddressProvided ?? false);


    // Preferred Contact Method Control
    this.preferredContactMethodControl.setValidators(this.fcListValidation.getValidators('PreferredContactMethod'));
    this.preferredContactMethodControl.setValue(this.listItem?.PreferredContactMethod ?? '');

    // Notes Control
    this.notesControl.setValidators(this.fcListValidation.getValidators('Notes'));
    this.notesControl.setValue(this.listItem?.Notes ?? '');
  }

  public onOpenCopyContactInfo(): void {
    const initialState = {
      mode: FcItemModalMode.New,
    };

    this.apModalService.showComponentModal(FcCopyContactInfoListModalComponent, {
      panelClass: ['emds-modal-medium', 'emds-modal-medium-fixed'],
      disableClose: true,
      initialState
    }).then((res: any) => {
      if (res.resultValue.result) {
        if (res.resultValue.result.address) {
          this.listItem.Address1 = res.resultValue.result.address;
        }

        if (res.resultValue.result.emailOne || res.resultValue.result.emailTwo) {
          const emailsValueFromPerson = [
            {
              email: res.resultValue.result.emailOne ?? '',
              marketing: this.listItem?.Email1MessagingAllowed ?? false,
            },
            {
              email: res.resultValue.result.emailTwo ?? '',
              marketing: this.listItem?.Email2MessagingAllowed ?? false,
            },
          ];

          this.emailsFormArray.patchValue(emailsValueFromPerson);
        }

        if (res.resultValue.result.phone) {
          const copiedPhonesValue = [
            {
              phoneNumber: this.formatPhoneNumber(res.resultValue.result.phone?.Phone1?.PhoneNumber),
              phoneType: res.resultValue.result.phone?.Phone1?.PhoneType ?? '',
              phoneTypeUid: res.resultValue.result.phone?.Phone1?.PhoneTypeUid ? [res.resultValue.result.phone?.Phone1.PhoneTypeUid] : [],
              marketing: this.listItem?.Phone1MessagingAllowed ?? false,
              isPhoneSmsEnabled: this.listItem?.Phone1SmsEnabledDefault ?? false
            },
            {
              phoneNumber: this.formatPhoneNumber(res.resultValue.result.phone?.Phone2?.PhoneNumber),
              phoneType: res.resultValue.result.phone?.Phone2?.PhoneType ?? '',
              phoneTypeUid: res.resultValue.result.phone?.Phone2?.PhoneTypeUid ? [res.resultValue.result.phone?.Phone2.PhoneTypeUid] : [],
              marketing: this.listItem?.Phone2MessagingAllowed ?? false,
              isPhoneSmsEnabled: this.listItem?.Phone2SmsEnabledDefault ?? false
            },
            {
              phoneNumber: this.formatPhoneNumber(res.resultValue.result.phone?.Phone3?.PhoneNumber),
              phoneType: res.resultValue.result.phone?.Phone3?.PhoneType ?? '',
              phoneTypeUid: res.resultValue.result.phone?.Phone3?.PhoneTypeUid ? [res.resultValue.result.phone?.Phone3.PhoneTypeUid] : [],
              marketing: this.listItem?.Phone3MessagingAllowed ?? false,
              isPhoneSmsEnabled: this.listItem?.Phone3SmsEnabledDefault ?? false
            },
            {
              phoneNumber: this.formatPhoneNumber(res.resultValue.result.phone?.Phone4?.PhoneNumber),
              phoneType: res.resultValue.result.phone?.Phone4?.PhoneType ?? '',
              phoneTypeUid: res.resultValue.result.phone?.Phone4?.PhoneTypeUid ? [res.resultValue.result.phone?.Phone4.PhoneTypeUid] : [],
              marketing: this.listItem?.Phone4MessagingAllowed ?? false,
              isPhoneSmsEnabled: this.listItem?.Phone4SmsEnabledDefault ?? false
            },
          ];

          this.phonesFormArray.patchValue(copiedPhonesValue);
        }
      }
    });
  }

  public subscribeEmails(): void {
    combineLatest([
      this.emailsFormArray.valueChanges,
      this.declinedOrHasNoEmailAddressControl.valueChanges
    ]).pipe(
      distinctUntilChanged(),
      this.takeUntilDestroy(),
    ).subscribe((values) => {
      const declinedOrHasNoEmailAddress = values[1];
      for (const emailFormGroup of this.emailsFormArray.controls) {
        const emailControl = emailFormGroup.get('email');
        const marketingControl = emailFormGroup.get('marketing');
        if (emailControl == null || typeof emailControl.value !== 'string' || marketingControl == null) return;

        if (declinedOrHasNoEmailAddress) {
          emailControl.setValue('', {emitEvent: false});
          emailControl.disable({emitEvent: false});
          marketingControl.setValue(false, {emitEvent: false});
          marketingControl.disable({emitEvent: false});
        } else {
          emailControl.enable({emitEvent: false});

          if (emailControl.value.length > 0 && marketingControl.disabled) {
            // Enable marketing when email is not empty
            marketingControl.enable({emitEvent: false});
          } else if (emailControl.value.length === 0) {
            // Disable marketing and uncheck when email is empty
            marketingControl.setValue(false, {emitEvent: false});
            marketingControl.disable({emitEvent: false});
          }
        }
      }
    });
  }

  public addControlsToForm(): void {
    this.form.addControl('nameDetails', this.nameDetailsFormGroup);
    this.form.addControl('otherInformation', this.otherInformationFormGroup);
    this.form.addControl('undeliverableToPrimaryAddress', this.undeliverableToPrimaryAddressControl);
    this.form.addControl('primaryAddress', this.primaryAddressFormGroup);
    this.form.addControl('secondaryAddress', this.secondaryAddressFormGroup);
    this.form.addControl('phones', this.phonesFormArray);
    this.form.addControl('emails', this.emailsFormArray);
    this.form.addControl('declinedOrHasNoEmailAddress', this.declinedOrHasNoEmailAddressControl);
    this.form.addControl('preferredContactMethod', this.preferredContactMethodControl);
    this.form.addControl('notes', this.notesControl);
  }

  public onPhoneTypeDataPopulate(
    phoneTypeListItems: FcPhoneTypeListItemDto[],
    phoneTypeControl: FormControl,
    smsEnabledControl: FormControl
  ): void {
    const [phoneTypeListItem] = phoneTypeListItems;
    if (phoneTypeListItem) {
      smsEnabledControl.setValue(phoneTypeListItem.SmsEnabledDefault);
    }
    if (phoneTypeListItem != null && typeof phoneTypeListItem.Name === 'string' && typeof phoneTypeListItem.PhoneTypeUid === 'string') {
      phoneTypeControl.setValue(phoneTypeListItem.Name);
    } else {
      phoneTypeControl.setValue('');
    }
  }

  public formatPhoneNumber(phoneNumberValue?: string): string {
    const regex = /^(\d{3}-\d{4})|(\(\d{3}\)\d{3}-\d{4})|(\(\d{3}\)\d{3}-\d{4} x\d{1,7})$/;
    if (typeof phoneNumberValue === 'string' && !regex.test(phoneNumberValue)) {
      switch (true) {
        case phoneNumberValue.length === 7:
          return phoneNumberValue?.replace(/(\d{3})(\d{4})/, '$1-$2');
        case phoneNumberValue.length === 10:
          return phoneNumberValue?.replace(/(\d{3})(\d{3})(\d{4})/, '($1)$2-$3');
        case phoneNumberValue.length > 10 && phoneNumberValue.length < 18:
          return phoneNumberValue?.replace(/^(\d{3})(\d{3})(\d{4})(\d{0,7})$/, '($1)$2-$3 x$4');
      }
    }
    return phoneNumberValue ?? '';
  }

  public isAddressPartiallyFilled(addressFormGroup: FormGroup): boolean {
    if (addressFormGroup == null || addressFormGroup.value == null) return false;

    const {addressOne, addressTwo, city, state, zip, country} = addressFormGroup.value;
    if (addressOne?.length > 0) return true;
    if (addressTwo?.length > 0) return true;
    if (city?.length > 0) return true;
    if (state?.length > 0) return true;
    if (zip?.length > 0) return true;
    if (country?.length > 0) return true;

    return false;
  }

  public addUniqueIdToBaseId(): void {
    const randomId = new Date().getTime();
    this.baseId += `-${randomId}`;
  }
}
